/**
 * useAccentColorEffect — applies personal accent color as CSS custom properties.
 *
 * Reads `#personal-color` from Dexie settings and converts the hex value
 * to oklch components, then sets `--primary-h` and `--primary-c` on the
 * document root. All theme colors derive from these two variables.
 *
 * Default: #FB4B97 (pink-500) = oklch(0.6813 0.2197 358.43)
 *
 * @see globals.css for the oklch-based theme system
 * @see SettingsAppearance.tsx for the color picker UI
 */

import { useEffect } from "react"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { hexToOklch } from "@/shared/utils/color/hexToOklch"

const DEFAULT_ACCENT = "#FB4B97"

export function useAccentColorEffect(): void {
  const { value: accentColor } = useSetting("#personal-color", DEFAULT_ACCENT)

  useEffect(() => {
    const root = document.documentElement
    const { c, h } = hexToOklch(accentColor)

    root.style.setProperty("--primary-h", String(h))
    root.style.setProperty("--primary-c", String(c))

    return () => {
      root.style.removeProperty("--primary-h")
      root.style.removeProperty("--primary-c")
    }
  }, [accentColor])
}
